/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import {
  Grid,
  CellMeasurer,
  CellMeasurerCache,
  ScrollParams,
  MultiGrid,
} from 'react-virtualized'
import clsx from 'clsx'
import { useRect } from '@reach/rect'
import {
  SortableContainer,
  SortableElement,
  SortEvent,
  SortEventWithTag,
  SortStartHandler,
} from 'react-sortable-hoc'
import { Map, fromJS, List } from 'immutable'
import arrayMove from 'array-move'

import { ColumnProps } from '../../types'
import HeaderCell from './components/HeaderCell'
import usePrevious from '../../hooks/usePrevious'
import useFocusedCell from '../../hooks/useFocusedCell'

const DEFAULT_COLUMN_WIDTH = 200
const CHECKBOX_ROW_WIDTH = 32
const HEADER_HEIGHT = 36

export interface Props<T> {
  data: T[] | List<T>
  onDataChange?: (
    updatedData: T[] | List<T>,
    {
      itemKey,
      dataKey,
      rowIndex,
      columnIndex,
      updatedValue,
    }: {
      itemKey: string
      dataKey: string
      rowIndex: number
      columnIndex: number
      updatedValue: T
    },
  ) => void
  columns: ColumnProps[]
  onColumnsChange?: (updatedColumns: ColumnProps[]) => void
  defaultColumnWidth?: number
  rowKey: string
  className?: string
  style?: React.CSSProperties
  id?: string

  gridId?: string
  gridClassName?: string
  gridStyle?: React.CSSProperties
  scrollToRow?: number
  scrollToColumn?: number
  scrollTop?: number
  scrollLeft?: number
  onScroll?: (params: ScrollParams) => void
  tabIndex?: number
  noContentRenderer?: () => React.ReactNode

  // head props
  resizableColumns?: boolean
  onResize?: (dataKey: string, width: number) => void
  headerGridId?: string
  headerGridClassName?: string
  headerGridStyle?: React.CSSProperties
  headerGridContainerStyle?: React.CSSProperties

  // selectable props
  selectable?: boolean | ((keys: string) => boolean)
  selectedKeys?: string[]
  onSelection?: (selectedKeys: string[]) => void
  renderCheckbox?: (checked: boolean, rowIndex: number) => React.ReactNode
  getCheckboxClassName?: (checked: boolean, rowIndex: number) => string
  getCheckboxStyle?: (checked: boolean, rowIndex: number) => React.CSSProperties
  checkboxGridClassName?: string
  checkboxGridStyle?: React.CSSProperties

  // reorderable props
  reorderableColumns?: boolean
  helperClassName?: string
  lockAxis?: boolean
  onReorderStart?: SortStartHandler
  lockToContainerEdges?: boolean
  shouldCancelReorderStart?: (event: SortEvent | SortEventWithTag) => boolean
  updateBeforeReorderStart?: SortStartHandler
}

const Table = <T extends Record<string, any> | Map<string, any>>({
  columns,
  onColumnsChange,
  data,
  onDataChange,
  defaultColumnWidth,
  id,
  className,
  style,
  resizableColumns,
  onResize,
  reorderableColumns,
  noContentRenderer,
  gridId,
  gridClassName,
  gridStyle,
  scrollToRow,
  scrollToColumn,
  scrollTop,
  scrollLeft: gridScrollLeft,
  onScroll,
  tabIndex,
  renderCheckbox,
  selectable,
  selectedKeys,
  onSelection,
  rowKey,
  shouldCancelReorderStart,
  updateBeforeReorderStart,
  onReorderStart,
  helperClassName,
  lockAxis,
  lockToContainerEdges,
  headerGridId,
  headerGridClassName,
  headerGridStyle,
  headerGridContainerStyle,
  getCheckboxClassName,
  getCheckboxStyle,
  checkboxGridClassName,
  checkboxGridStyle,
}: Props<T>) => {
  const headerGrid = useRef<Grid | null>(null)
  const body = useRef<HTMLDivElement | null>(null)
  const bodyGrid = useRef<MultiGrid | null>(null)

  const cellMeasurerCache = new CellMeasurerCache({
    fixedWidth: true,
    defaultHeight: 30,
  })

  const prevColumns = usePrevious(columns)

  const [columnsWidth, setColumnsWidth] = useState<Map<string, number>>(
    Map(
      columns.reduce(
        (acc, column) => ({
          ...acc,
          [column.dataKey]:
            column.width || defaultColumnWidth || DEFAULT_COLUMN_WIDTH,
        }),
        {},
      ),
    ),
  )
  const prevColumnsWidth = usePrevious(columnsWidth)
  const totalColumnsWidth = Array.from(columnsWidth.values()).reduce(
    (acc, columnWidth) => acc + columnWidth,
    0,
  )

  useEffect(() => {
    if (columnsWidth !== prevColumnsWidth || columns !== prevColumns) {
      const columnsChanged: number[] = []
      for (const key of Array.from(columnsWidth.keys())) {
        if (columnsWidth.get(key) !== prevColumnsWidth.get(key)) {
          cellMeasurerCache.clearAll()
          columnsChanged.push(
            columns.findIndex((column) => column.dataKey === key),
          )
          if (onResize) {
            onResize(key, columnsWidth.get(key) || DEFAULT_COLUMN_WIDTH)
          }
        }
      }
      for (const columnChanged of columnsChanged) {
        headerGrid.current?.recomputeGridSize({ columnIndex: columnChanged })
        bodyGrid.current?.recomputeGridSize({
          columnIndex: columnChanged + (selectable ? 1 : 0),
        })
      }
    }
  }, [
    columnsWidth,
    prevColumnsWidth,
    columns,
    prevColumns,
    onResize,
    selectable,
  ])

  const container = useRef<HTMLDivElement | null>(null)
  const rect = useRect(container)

  const [horizontalScrollbarHeight, setHorizontalScrollbarHeight] = useState(0)
  const [verticalScrollbarWidth, setVerticalScrollbarWidth] = useState(0)

  const [scrollLeft, setScrollLeft] = useState(gridScrollLeft || 0)

  const gridSize = rect
    ? Math.min(
        rect.width,
        totalColumnsWidth +
          (selectable ? CHECKBOX_ROW_WIDTH : 0) +
          (horizontalScrollbarHeight || verticalScrollbarWidth),
      )
    : null

  const handleColumnResize = useCallback(
    (columnDataKey: string) => (delta: number) => {
      setColumnsWidth((columnsWidthState) => {
        return columnsWidthState.set(
          columnDataKey,
          Math.min(
            Math.max(
              columnsWidthState.get(columnDataKey, 0) + delta,
              columns.find((column) => column.dataKey === columnDataKey)
                ?.minWidth || 80,
            ),
            columns.find((column) => column.dataKey === columnDataKey)
              ?.maxWidth || 300,
          ),
        )
      })
    },
    [columns],
  )

  const [sortable, setSortable] = useState<{
    column?: string
    order?: 'desc' | 'asc'
  }>({ column: undefined, order: undefined })

  const [dataToRender, setDataToRender] = useState<List<Map<string, any>>>(
    Array.isArray(data) ? fromJS(data) : data,
  )

  const {
    focusedCell,
    updateFocusedCellColumnIndex,
    handleKeydown,
    navigateTo,
    editMode,
  } = useFocusedCell({
    columnCount: columns.length,
    rowCount: dataToRender.size,
    editableColumn: columns.map((column) => !!column.editable),
  })

  useEffect(() => {
    if (typeof scrollToRow === 'number' && typeof scrollToColumn === 'number') {
      navigateTo({ rowIndex: scrollToRow, columnIndex: scrollToColumn })()
    } else if (typeof scrollToRow === 'number') {
      navigateTo({ rowIndex: scrollToRow })()
    } else if (typeof scrollToColumn === 'number') {
      navigateTo({ columnIndex: scrollToColumn })()
    }
  }, [scrollToColumn, scrollToRow])

  useEffect(() => {
    if (!editMode) {
      body.current?.focus()
    }
  }, [editMode])

  const prevData = usePrevious(data)
  const prevEditMode = usePrevious(editMode)

  useEffect(() => {
    if ((prevData !== data && !editMode) || (prevEditMode && !editMode)) {
      const immutableData: List<Map<string, any>> = Array.isArray(data)
        ? fromJS(data)
        : data
      const sortableColumn = sortable.order
        ? columns.find((column) => column.dataKey === sortable.column)
        : undefined
      if (sortableColumn) {
        setDataToRender(
          immutableData.sortBy(
            (item) => {
              if (sortableColumn.sortBy) {
                return sortableColumn.sortBy(item.get(sortableColumn.dataKey))
              }
              return item.get(sortableColumn.dataKey)
            },
            (a: number | string, b: number | string) => {
              if (typeof a === 'number' && typeof b === 'number') {
                return sortable.order === 'asc' ? a - b : b - a
              }

              if (typeof a === 'string' && typeof b === 'string') {
                return sortable.order === 'asc'
                  ? a.localeCompare(b)
                  : b.localeCompare(a)
              }
              return 0
            },
          ),
        )
      } else {
        setDataToRender(Array.isArray(data) ? fromJS(data) : data)
      }
      cellMeasurerCache.clearAll()
      bodyGrid.current?.recomputeGridSize()
    }
  }, [prevData, data, sortable, columns, editMode, prevEditMode])

  const handleSortChange = useCallback(
    (columnDataKey) => (order?: 'desc' | 'asc') => {
      if (order) {
        setSortable({ column: columnDataKey, order })
        const sortableColumn = columns.find(
          (column) => column.dataKey === columnDataKey,
        )
        if (sortableColumn) {
          setDataToRender((dataToRenderState) =>
            dataToRenderState.sortBy(
              (item) => {
                if (sortableColumn.sortBy) {
                  return sortableColumn.sortBy(item.get(sortableColumn.dataKey))
                }
                return item.get(sortableColumn.dataKey)
              },
              (a: number | string, b: number | string) => {
                if (typeof a === 'number' && typeof b === 'number') {
                  return order === 'asc' ? a - b : b - a
                }

                if (typeof a === 'string' && typeof b === 'string') {
                  return order === 'asc'
                    ? a.localeCompare(b)
                    : b.localeCompare(a)
                }
                return 0
              },
            ),
          )
        }
      } else {
        setSortable({ column: undefined, order: undefined })
        setDataToRender(fromJS(data))
      }
    },
    [columns, data],
  )

  const getColumnWidth = useCallback(
    ({ index }) => {
      if (selectable) {
        return index !== 0
          ? columnsWidth.get(columns[index - 1].dataKey, 0)
          : CHECKBOX_ROW_WIDTH
      }
      return columnsWidth.get(columns[index].dataKey, 0)
    },
    [columnsWidth, columns, selectable],
  )

  const getHeaderColumnWidth = useCallback(
    ({ index }) => columnsWidth.get(columns[index].dataKey, 0),
    [columnsWidth, columns],
  )

  const handleMouseDown = useCallback(
    ({
      focused,
      rowIndex,
      columnIndex,
    }: {
      focused: boolean
      rowIndex: number
      columnIndex: number
    }) => () => {
      if (!focused || !editMode) {
        navigateTo({ rowIndex, columnIndex })()
      }
    },
    [editMode, navigateTo],
  )

  const handleDataChange = useCallback(
    ({
      itemKey,
      dataKey,
      rowIndex,
      columnIndex,
    }: {
      itemKey: string
      dataKey: string
      rowIndex: number
      columnIndex: number
    }) => (updatedValue: any) => {
      setDataToRender((dataToRenderState) => {
        const item = dataToRenderState.get(rowIndex)?.set(dataKey, updatedValue)
        let updatedData = dataToRenderState

        if (item) {
          updatedData = dataToRenderState.set(rowIndex, item)
        }

        if (onDataChange) {
          onDataChange(updatedData as List<T>, {
            itemKey,
            dataKey,
            rowIndex,
            columnIndex,
            updatedValue,
          })
        }
        return updatedData
      })
    },
    [onDataChange, setDataToRender],
  )

  const headerCellRenderer = useCallback(
    ({ key, style, columnIndex }) => {
      const column = columns[columnIndex]

      return (
        <HeaderCellComponent
          className={column.headerClassName}
          key={key}
          style={{ ...style, ...column.headerStyle }}
          title={column.title}
          index={columnIndex}
          resizable={resizableColumns && !column.disableResize}
          onResize={handleColumnResize(column.dataKey)}
          sortable={column.sortable}
          order={
            sortable.column === column.dataKey ? sortable.order : undefined
          }
          disabled={column.disableReorder}
          onSortChange={handleSortChange(column.dataKey)}
        />
      )
    },
    [columns, resizableColumns, handleColumnResize, handleSortChange, sortable],
  )

  const setHeaderGridRef = useCallback(
    (ref) => {
      if (reorderableColumns && ref) {
        // @ts-ignore
        headerGrid.current = ref.getWrappedInstance()
      } else if (ref instanceof Grid) {
        headerGrid.current = ref
      }
    },
    [reorderableColumns, headerGrid],
  )

  const handleReorder = useCallback(
    ({ oldIndex, newIndex }) => {
      if (oldIndex !== newIndex && onColumnsChange) {
        onColumnsChange(arrayMove(columns, oldIndex, newIndex))
        updateFocusedCellColumnIndex(newIndex)

        headerGrid.current?.recomputeGridSize({ columnIndex: oldIndex })
        headerGrid.current?.recomputeGridSize({ columnIndex: newIndex })
        bodyGrid.current?.recomputeGridSize({
          columnIndex: newIndex + (selectable ? 1 : 0),
        })
        bodyGrid.current?.recomputeGridSize({
          columnIndex: oldIndex + (selectable ? 1 : 0),
        })
      }
    },
    [onColumnsChange, updateFocusedCellColumnIndex, columns, selectable],
  )

  const shouldCancelStart = useCallback(
    (event) => {
      const target = event.target as HTMLElement

      if (
        target.tagName === 'BUTTON' ||
        target.classList.contains('buttons-container')
      ) {
        return true
      }

      if (shouldCancelReorderStart) {
        return shouldCancelReorderStart(event)
      }

      return false
    },
    [shouldCancelReorderStart],
  )

  const handleCheckboxToggle = useCallback(
    (itemKey) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onSelection) {
        onSelection(
          event.target.checked
            ? [...(selectedKeys || []), itemKey]
            : selectedKeys?.filter((key) => key !== itemKey) || [],
        )
      }
    },
    [onSelection, selectedKeys],
  )

  useEffect(() => {
    bodyGrid.current?.forceUpdateGrids()
  }, [selectedKeys])

  const cellRenderer = useCallback(
    ({ key, parent, style: cellStyle, columnIndex, rowIndex, isScrolling }) => {
      console.log(columnIndex, rowIndex, cellStyle)
      const itemKey = dataToRender.get(rowIndex)?.get(rowKey)
      const rowChecked = selectedKeys?.includes(itemKey)

      if (selectable && columnIndex === 0) {
        return (
          <CellMeasurer
            key={key}
            parent={parent}
            columnIndex={columnIndex}
            rowIndex={rowIndex}
            cache={cellMeasurerCache}
          >
            <div
              style={
                getCheckboxStyle
                  ? {
                      ...cellStyle,
                      ...getCheckboxStyle(rowChecked || false, rowIndex),
                    }
                  : cellStyle
              }
              className={clsx(
                'flex justify-center py-2 items-center ',
                rowChecked ? 'opacity-100' : 'opacity-25 hover:opacity-100',
                getCheckboxClassName
                  ? getCheckboxClassName(rowChecked || false, rowIndex)
                  : undefined,
              )}
            >
              {renderCheckbox ? (
                renderCheckbox(rowChecked || false, rowIndex)
              ) : (
                <input
                  className="cursor-pointer"
                  type="checkbox"
                  checked={rowChecked}
                  onChange={handleCheckboxToggle(itemKey)}
                />
              )}
            </div>
          </CellMeasurer>
        )
      }

      const newColumnIndex = columnIndex - (selectable ? 1 : 0)
      const column = columns[newColumnIndex]

      const {
        renderPlaceholder,
        renderCell,
        renderEditor: Editor,
        dataKey,
        getCellClassName,
        getCellStyle,
        editable,
        editorClassName,
        editorProps,
        editorStyle,
      } = column

      const value = dataToRender.get(rowIndex)?.get(dataKey)

      const content =
        isScrolling && renderPlaceholder
          ? renderPlaceholder(value, rowIndex)
          : renderCell(value, rowIndex)

      const focused =
        focusedCell.rowIndex === rowIndex &&
        focusedCell.columnIndex === newColumnIndex

      const cell = (
        <div
          id={column.cellId}
          style={
            getCellStyle
              ? {
                  ...cellStyle,
                  ...getCellStyle(
                    focused,
                    rowChecked || false,
                    focused && editMode,
                  ),
                }
              : cellStyle
          }
          className={clsx(
            'p-2 border text-sm text-gray-600 break-all',
            rowChecked ? 'border-blue-200 bg-blue-100' : 'border-gray-200',
            focused ? 'bg-blue-200 border-blue-300' : undefined,
            getCellClassName
              ? getCellClassName(
                  focused,
                  rowChecked || false,
                  focused && editMode,
                )
              : undefined,
          )}
          key={key}
          onClick={handleMouseDown({
            focused,
            rowIndex,
            columnIndex: newColumnIndex,
          })}
        >
          {editable && Editor && focused && editMode ? (
            <Editor
              value={value}
              onChange={handleDataChange({
                itemKey,
                dataKey,
                rowIndex,
                columnIndex: newColumnIndex,
              })}
              style={editorStyle}
              className={editorClassName}
              {...editorProps}
            />
          ) : (
            content
          )}
        </div>
      )

      if (
        !cellMeasurerCache.has(rowIndex, columnIndex) ||
        (focused && editMode)
      ) {
        return (
          <CellMeasurer
            cache={cellMeasurerCache}
            key={key}
            parent={parent}
            columnIndex={columnIndex}
            rowIndex={rowIndex}
          >
            {cell}
          </CellMeasurer>
        )
      }

      return cell
    },
    [
      columns,
      dataToRender,
      focusedCell,
      rowKey,
      selectable,
      selectedKeys,
      editMode,
      handleMouseDown,
      handleDataChange,
      cellMeasurerCache,
      getCheckboxClassName,
      getCheckboxStyle,
      renderCheckbox,
      handleCheckboxToggle,
    ],
  )

  const handleScroll = useCallback(
    (params: ScrollParams) => {
      if (onScroll) {
        onScroll(params)
      }
      setScrollLeft(params.scrollLeft)
    },
    [onScroll],
  )

  const onScrollbarPresenceChange = useCallback(
    ({ horizontal, vertical, size }) => {
      setHorizontalScrollbarHeight(horizontal ? size : 0)
      setVerticalScrollbarWidth(vertical ? size : 0)
    },
    [],
  )

  const HeaderGridComponent = useMemo(() => {
    if (reorderableColumns) {
      return SortableContainer(Grid, { withRef: true })
    }
    return Grid
  }, [reorderableColumns])

  const HeaderCellComponent = useMemo(() => {
    if (reorderableColumns) {
      return SortableElement(HeaderCell)
    }
    return HeaderCell
  }, [reorderableColumns])

  return (
    <div
      id={id}
      className={clsx('flex flex-col', className)}
      style={style}
      ref={container}
    >
      <div
        style={{
          height: HEADER_HEIGHT,
          width: gridSize
            ? gridSize - (selectable ? CHECKBOX_ROW_WIDTH : 0)
            : '100%',
          marginLeft: selectable ? CHECKBOX_ROW_WIDTH : 0,
        }}
      >
        <HeaderGridComponent
          id={headerGridId || 'headerGrid'}
          className={clsx('focus:outline-none', headerGridClassName)}
          style={{
            overflowX: 'hidden',
            overflowY: 'hidden',
            ...headerGridStyle,
          }}
          containerStyle={headerGridContainerStyle}
          width={
            gridSize
              ? gridSize -
                (selectable ? CHECKBOX_ROW_WIDTH : 0) -
                verticalScrollbarWidth
              : 0
          }
          height={HEADER_HEIGHT}
          columnCount={columns.length}
          rowCount={1}
          columnWidth={getHeaderColumnWidth}
          rowHeight={HEADER_HEIGHT}
          cellRenderer={headerCellRenderer}
          scrollLeft={scrollLeft}
          helperClass={helperClassName}
          axis="x"
          lockAxis={lockAxis ? 'x' : undefined}
          ref={setHeaderGridRef}
          onSortEnd={handleReorder}
          onSortStart={onReorderStart}
          shouldCancelStart={shouldCancelStart}
          updateBeforeSortStart={updateBeforeReorderStart}
          lockToContainerEdges={lockToContainerEdges}
        />
      </div>
      <div
        className="flex outline-none"
        ref={body}
        onKeyDown={handleKeydown}
        tabIndex={0}
      >
        <MultiGrid
          id={gridId || 'bodyGrid'}
          ref={bodyGrid}
          classNameBottomRightGrid={clsx('focus:outline-none', gridClassName)}
          classNameBottomLeftGrid={checkboxGridClassName}
          styleBottomLeftGrid={checkboxGridStyle}
          styleBottomRightGrid={gridStyle}
          fixedColumnCount={selectable ? 1 : 0}
          width={gridSize || 0}
          height={rect ? rect.height - HEADER_HEIGHT : 0}
          columnCount={columns.length + (selectable ? 1 : 0)}
          rowCount={dataToRender.size}
          columnWidth={getColumnWidth}
          rowHeight={cellMeasurerCache.rowHeight}
          cellRenderer={cellRenderer}
          onScrollbarPresenceChange={onScrollbarPresenceChange}
          onScroll={handleScroll}
          scrollTop={scrollTop}
          scrollLeft={scrollLeft}
          scrollToRow={
            usePrevious(focusedCell).rowIndex !== focusedCell.rowIndex
              ? focusedCell.rowIndex
              : undefined
          }
          scrollToColumn={
            usePrevious(focusedCell).columnIndex !== focusedCell.columnIndex
              ? focusedCell.columnIndex + (selectable ? 1 : 0)
              : undefined
          }
          sortOrder={sortable.column ? sortable.order : undefined}
          noContentRenderer={noContentRenderer}
          tabIndex={tabIndex}
        />
      </div>
    </div>
  )
}

Table.defaultProps = {
  defaultColumnWidth: DEFAULT_COLUMN_WIDTH,
}

export default Table
